
import ConfirmBox from "components/shared/VmwareMappingConfirmBox";
import { useState, useEffect } from "react";
import Breadcrumb from "components/shared/Breadcrumb";
import {getMatrixId} from "utils/matriceHelpers";
import vmwareService from "services/admin/masters/vmwareService";
import Select from 'react-select';
import MaterialTable from 'material-table';
import { toast } from "react-toastify";
import { MAPPING_SELECT_ERROR_TEMPLATE } from "constants/admin/masters/vmware";
import { ERROR_MESSAGE_UNKNOWN, MSG_RECORD_CREATE, MSG_RECORD_DELETE, MSG_RECORD_UNDO_DELETE, MSG_RECORD_UPDATE } from "constants/common";

function ManageVmwareManagementSwitch() {

  const [breamcrumbs, setBreamcrumbs] = useState([
    { title: "UCP VMware Masters Mapping", url: "/admin/master/ucp-vmware/" },
    { title: "Manage Mapping" },
  ]);

  const serviceObj = new vmwareService();
  const [switchList, setSwitchList] = useState([]);
  const [combineKeyList, setCombineKeyList] = useState([]);
  const [switchFirmwareList, setSwitchFirmwareList] = useState([]);
  const [switchMappingList, setSwitchMappingList] = useState([{}]);
  const [selectedCombineKeys, setSelectedCombineKeys] = useState([]);
  const [selectedSwitch, setSelectedSwitch] = useState(null);
  const [selectedFirmware, setSelectedFirmware] = useState(null);
  const [combineKeyError, setCombineKeyError] = useState('');
  const [switchError, setSwitchError] = useState('');
  const [firmwareError, setFirmwareError] = useState('');
  const [isFormTouched, setIsFormTouched] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [itemToBeEdited, setItemToBeEdited] = useState(null);
  const [isDeleteConfirmationShown, setIsDeleteConfirmationShown] = useState(false);
  const [isRestoreConfirmationShown, setIsRestoreConfirmationShown] = useState(false);
  const [deleteConfirmationMessage, setDeleteConfirmationMessage] = useState("");
  const [restoreConfirmationMessage, setRestoreConfirmationMessage] = useState("");
  const [itemToBeDeletedOrRestored, setItemToBeDeletedOrRestored] = useState(null);

  const  Id  = getMatrixId(window.location.pathname);

  useEffect(() => getSwitchData(), []);

  useEffect(() => {
    // re-validate form upon field value change if form is touched
    if (isFormTouched) {
      validateSwitchForm();
    }
  }, [selectedCombineKeys, selectedSwitch, selectedFirmware]);

  useEffect(() => {
    // populate details of item to be edited
    if (isEditMode) {
      populateItemDetailsToEdit();
    }
  }, [itemToBeEdited?.record_details?.id]);

  const getSwitchData = () => {
    serviceObj.getAllInitialData(Id)
      .then((response) => {
        extractAndSetCombineKeys(response?.vmware_combine_keys);
        extractAndSetSwitchList(response?.managementswitch_drop_list);
        setSwitchMappingList(response?.vmware_management_switch_mapping_list);
      })
      .catch((error) => {
        toast.error(ERROR_MESSAGE_UNKNOWN);
      });
  };

  // setting Combine keys
  const extractAndSetCombineKeys = (dataList) => {
    const combineKeyList = dataList?.map((item) => ({
      id: item?.id,
      value: item?.combine_key,
      label: item?.combine_key,
      disabled: item?.disabled
    }));
    setCombineKeyList(combineKeyList);
  };

  // setting Management Switch list
  const extractAndSetSwitchList = (dataList) => {
    const switchList = dataList?.map(item => ({
      id: item?.id,
      value: item?.name,
      label: item?.name,
      disabled: item?.disabled
    }));
    setSwitchList(switchList);
  };

  // setting Management Switch Firmware list
  const extractAndSetFirmwareList = (dataList) => {
    const firmwareList = dataList?.map(item => ({
      id: item?.id,
      value: item?.name,
      label: item?.name,
      disabled: item?.disabled
    }));
    setSwitchFirmwareList(firmwareList);
  };

  // set selected switch and get corresponding firmware
  const switchSelectionHandler = (selectedSwitch) => {
    setSelectedSwitch(selectedSwitch);
    getFirmwareBySwitch(selectedSwitch);
  };

  // get corresponding firmware by selected switch
  const getFirmwareBySwitch = (selectedSwitch) => {
    serviceObj.getFirmwareBySwitch(Id, selectedSwitch?.id)
      .then(response => extractAndSetFirmwareList(response?.switch_firmware_list))
      .catch(error => toast.error(ERROR_MESSAGE_UNKNOWN));
  };

  // reset form values, error messages, form state, edit mode, target edit item
  const resetForm = () => {
    resetFormValues();
    resetErrorMessages();
    setIsFormTouched(false);
    setIsEditMode(false);
    setItemToBeEdited(null);
  };

  // append offending field name in error message
  const getErrorMessageFor = (fieldName) => {
    return MAPPING_SELECT_ERROR_TEMPLATE?.replace("_FIELD_NAME_", fieldName);
  };

  // validate fields and set or reset error message
  const validateSwitchForm = () => {
    let isFormValid = true;
    if (!selectedCombineKeys?.length) {
      setCombineKeyError(getErrorMessageFor("Combine Key"));
      isFormValid = false;
    } else {
      setCombineKeyError("");
    }

    if (!selectedSwitch?.value) {
      setSwitchError(getErrorMessageFor("Management Switch"));
      isFormValid = false;
    } else {
      setSwitchError("");
    }

    if (!selectedFirmware?.value) {
      setFirmwareError(getErrorMessageFor("Management Switch Firmware"));
      isFormValid = false;
    } else {
      setFirmwareError("");
    }

    return isFormValid;
  };

  // reset field values
  const resetFormValues = () => {
    setSelectedCombineKeys([]);
    setSelectedSwitch(null);
    setSelectedFirmware(null);
    setSwitchFirmwareList([]); // empty firmware list s no switch is selected upon reset
  };

  // reset error messages
  const resetErrorMessages = () => {
    setCombineKeyError("");
    setSwitchError("");
    setFirmwareError("");
  };

  // api call for adding new mapping
  const addSwitchMapping = (e) => {
    e.preventDefault();
    setIsFormTouched(true);
    const isFormValid = validateSwitchForm();

    // call POST api if form is valid
    if (isFormValid) {
      let payload = {
        management_switch_id: selectedSwitch?.id,
        management_switch_firmware_id: selectedFirmware?.id,
        vmware_combine_keys: selectedCombineKeys?.map(key => key?.id)
      };
      serviceObj.createManagementSwitchMapping(payload).then((response) => {
        toast.success(MSG_RECORD_CREATE);
        getSwitchData();
        resetForm();
      })
      .catch(error => toast.error(ERROR_MESSAGE_UNKNOWN));
    }
  };

  // process target item for editing
  const populateItemDetailsToEdit = () => {
    const combineKeysToEdit = itemToBeEdited?.vmware_combine_keys?.map((item) => ({
      id: item?.id,
      value: item?.combine_key,
      label: item?.combine_key,
      disabled: item?.disabled
    }));
    const switchToEdit = {
      id: itemToBeEdited?.record_details?.management_switch_id,
      value: itemToBeEdited?.record_details?.management_switch_name,
      label: itemToBeEdited?.record_details?.management_switch_name
    };
    const firmwareToEdit = {
      id: itemToBeEdited?.record_details?.management_switch_firmware_id,
      value: itemToBeEdited?.record_details?.management_switch_firmware_name,
      label: itemToBeEdited?.record_details?.management_switch_firmware_name
    };
    setSelectedCombineKeys(combineKeysToEdit);
    setSelectedSwitch(switchToEdit);
    setSelectedFirmware(firmwareToEdit);
  };

  // fetch details of item to be edited
  const editSwitchMapping = (data) => {
    setIsEditMode(true);
    // fetch details of item to edit
    serviceObj.getManagementSwitchMappingDetails(data?.id).then((response) => {
      setItemToBeEdited(response);
    })
      .catch(error => toast.error(ERROR_MESSAGE_UNKNOWN));
  };

  // update Management Switch
  const updateSwitchMappingById = (e) => {
    e.preventDefault();
    setIsFormTouched(true);
    const isFormValid = validateSwitchForm();
    if (isFormValid) {
      const payload = {
        management_switch_id: selectedSwitch?.id,
        management_switch_firmware_id: selectedFirmware?.id,
        vmware_combine_keys: selectedCombineKeys?.map(key => key?.id)
      };
      const targetItemId = itemToBeEdited?.record_details?.id;
      serviceObj.updateManagementSwitchMappingById(targetItemId, payload).then((response) => {
        toast.success(MSG_RECORD_UPDATE);
        getSwitchData();
        resetForm();
      })
        .catch(error => toast.error(ERROR_MESSAGE_UNKNOWN)
        );
    }
  };

  // delete or restore mapping
  const deleteOrRestoreSwitchById = () => {
    const { id, is_deleted } = itemToBeDeletedOrRestored;
    serviceObj.deleteManagementSwitchMappingById(id)
      .then((response) => {
        // show deleted or restored message based on is_deleted property
        if (is_deleted) {
          toast.success(MSG_RECORD_UNDO_DELETE);
          setIsRestoreConfirmationShown(false);
          setRestoreConfirmationMessage("")
        } else {
          toast.success(MSG_RECORD_DELETE);
          setIsDeleteConfirmationShown(false)
          setDeleteConfirmationMessage("");
        }
        resetForm();
        setItemToBeDeletedOrRestored(null);
      })
      .catch((error) => {
        toast.error(ERROR_MESSAGE_UNKNOWN);
        setIsDeleteConfirmationShown(false)
        setDeleteConfirmationMessage("");
        setItemToBeDeletedOrRestored(null);
      })
      .finally(() => getSwitchData());
  };

  // show delete warning confirmation popup
  const deleteMappingHandler = (rowData) => {
    setIsDeleteConfirmationShown(true);
    setItemToBeDeletedOrRestored(rowData);
    setDeleteConfirmationMessage(`Are you sure you want to remove ${rowData?.management_switch_name} with Combine Key ${rowData?.vmware_combine_key}?`)
  };

  // show restore warning confirmation popup
  const restoreMappingHandler = (rowData) => {
    setIsRestoreConfirmationShown(true);
    setItemToBeDeletedOrRestored(rowData);
    setRestoreConfirmationMessage(`Are you sure you want to restore ${rowData?.management_switch_name} with Combine Key ${rowData?.vmware_combine_key}?`);
  };

  // hide confirmation message and reset target item
  const cancelDeleteItem = () => {
    setIsDeleteConfirmationShown(false);
    setItemToBeDeletedOrRestored(null);
  };

  // hide confirmation message and reset target item
  const cancelRestoreItem = () => {
    setIsRestoreConfirmationShown(false);
    setItemToBeDeletedOrRestored(null);
    setRestoreConfirmationMessage("")
  };

  return (
    <div className="form-wrap mt-3">
      <Breadcrumb data={breamcrumbs} disableEditTitle={true} />
      {isDeleteConfirmationShown &&
        <ConfirmBox
          actionHandler={deleteOrRestoreSwitchById}
          handleSubmit={deleteOrRestoreSwitchById}
          cancelDeleteItem={cancelDeleteItem}
        >
          {deleteConfirmationMessage}
        </ConfirmBox>
      }
      {isRestoreConfirmationShown &&
        <ConfirmBox
          actionHandler={deleteOrRestoreSwitchById}
          handleSubmit={deleteOrRestoreSwitchById}
          cancelDeleteItem={cancelRestoreItem}
        >
          {restoreConfirmationMessage}
        </ConfirmBox>
      }
      <div className="form-group d-flex">
        <div className="col-sm-6">
          <form>
            <div className="row pl-2 pb-1">
              <div className="col-sm-11">
                <div className="form-group">
                  <label htmlFor={""} className="mr-sm-2 mt-2"><b>VMware Combine Key</b></label>
                  {/* add multi select dropdown */}
                  <Select
                    isMulti
                    name="Combine Key"
                    options={combineKeyList}
                    classNamePrefix="select"
                    onChange={selectedKeys => setSelectedCombineKeys(selectedKeys)}
                    value={selectedCombineKeys}
                    isOptionDisabled={option => option.disabled}
                  />
                  {
                    combineKeyError
                    &&
                    <div className="col-sm-10 mb-2 text-danger">{combineKeyError}</div>
                  }
                </div>
                <div className="form-group">
                  <label htmlFor={"switch"} className="mr-sm-2"><b>Management Switch</b></label>
                  <Select
                    name="Management Switch"
                    id="switch"
                    options={switchList}
                    classNamePrefix="select"
                    onChange={selectedSwitch => switchSelectionHandler(selectedSwitch)}
                    isDisabled={false}
                    value={selectedSwitch}
                    isOptionDisabled={option => option.disabled}
                  />
                  {switchError
                    &&
                    <div className="col-sm-10 mb-2 text-danger">{switchError}</div>
                  }
                </div>
                <div className="form-group">
                  <label htmlFor={"switchFirmware"} className="mr-sm-2"><b>Management Switch Firmware</b></label>
                  <Select
                    name="Management Switch Firmware"
                    id="switchFirmware"
                    options={switchFirmwareList}
                    classNamePrefix="select"
                    onChange={selectedFirmware => setSelectedFirmware(selectedFirmware)}
                    isDisabled={false}
                    value={selectedFirmware}
                    isOptionDisabled={option => option.disabled}
                  />
                  {firmwareError
                    &&
                    <div className="col-sm-10 mb-2 text-danger">{firmwareError}</div>
                  }
                </div>
              </div>
            </div>

            <div className="col-sm-12">
              <button type="reset"
                onClick={resetForm}
                className="btn btn-md btn-light btn-link submit-btn-inline mr-sm-2"><i className="fa fa-refresh"></i> Reset</button>
              <button
                type="submit"
                className="btn btn-md btn-dark submit-btn-inline"
                onClick={isEditMode ? updateSwitchMappingById : addSwitchMapping}
              >
                {isEditMode ? "Update" : "Add"}
              </button>
            </div>
          </form>
        </div>
        <div className="col-sm-6">
          <MaterialTable
            title="Management Switch Mappings"
            columns={[
              { title: "Combine Key", field: 'vmware_combine_key' },
              { title: 'Management Switch', field: 'management_switch_name' },
              { title: 'Management Switch Firmware', field: 'management_switch_firmware_name' },
            ]}
            data={switchMappingList}
            options={{
              headerStyle: {
                backgroundColor: '#e9ecef',
              },
              actionsColumnIndex: -1,
              selection: false,
              search: true,
              maxBodyHeight: 400

            }}

            localization={{
              header: {
                actions: 'Actions',
              },
            }}
            actions={
              [
                (rowData) => ({
                  icon: "edit",
                  tooltip: !rowData.is_deleted && "Edit Record",
                  onClick: (e, data) => editSwitchMapping(data),
                  hidden: !rowData?.id || rowData.is_deleted // display action only if any valid item is present
                }),
                (rowData) => ({
                  icon: rowData.is_deleted ? "undo" : "delete",
                  tooltip: rowData.is_deleted ? "Undo Deleted Record" : "Delete Record",
                  onClick: (e, data) => rowData.is_deleted ? restoreMappingHandler(data) : deleteMappingHandler(data),
                  hidden: !rowData?.id // display action only if any valid item is present
                })
              ]}
          />
        </div>
      </div>
    </div>
  )
}

export default ManageVmwareManagementSwitch;